import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { GetPostsParams, PostsApi } from 'app/modules/posts/api/posts';
import { usePostsCreateNew } from 'app/modules/posts/hooks/usePostsCreateNew';
import { usePage } from 'modules/page/pageSelector';
import { useUser } from 'modules/user/userSelector';
import { getUserFirstTimeFlagStatus } from 'utils/user/firstTimeFlags';
import { FirstTimeFlagKey } from 'types/User';
import { OrderDirection } from 'app/types';
import routes, { modalRoutes } from 'constants/routes';
import { queryKey } from 'constants/queryKey';
import { ChecklistTaskHookResult } from 'app/modules/onboardingChecklist/types';
import { hasAnyPostSentForApproval } from 'app/modules/onboardingChecklist/utils/sentForApprovalStorage';

export const useSendPostForApprovalTask = (): ChecklistTaskHookResult => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createPost, isInPostsModule } = usePostsCreateNew();
  const { pages } = usePage();
  const { id: userId, firstTimeFlags } = useUser();

  const hasCreatedPost = getUserFirstTimeFlagStatus(
    firstTimeFlags,
    FirstTimeFlagKey.FIRST_POST_CREATED,
  );
  const hasSentPostForApproval =
    getUserFirstTimeFlagStatus(
      firstTimeFlags,
      FirstTimeFlagKey.FIRST_CLIENT_APPROVAL_REQUESTED,
    ) ||
    getUserFirstTimeFlagStatus(
      firstTimeFlags,
      FirstTimeFlagKey.FIRST_INTERNAL_APPROVAL_REQUESTED,
    ) ||
    hasAnyPostSentForApproval(userId);

  const profileIds = useMemo(() => pages.map((page) => page.id), [pages]);

  const postsParams: GetPostsParams = useMemo(
    () => ({
      dateFrom: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
      dateTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
      profiles: profileIds,
      profileGroups: [],
      statuses: [],
      labels: [],
      assignedUsers: [],
      pseudoTypes: [],
      projects: [],
      limit: 1,
      page: 1,
      orderDirection: OrderDirection.Asc,
      persistFilters: false,
    }),
    [profileIds],
  );

  const { data: postsData } = useQuery(
    queryKey.onboardingChecklistPosts(postsParams),
    () => PostsApi.posts(postsParams),
    {
      enabled:
        !hasCreatedPost && !hasSentPostForApproval && profileIds.length > 0,
    },
  );

  const hasAnyPost = hasCreatedPost || (postsData?.data?.length ?? 0) > 0;

  const handleSendForApproval = () => {
    navigate({
      pathname: isInPostsModule() ? undefined : routes.POSTS_CALENDAR,
      hash: modalRoutes.SEND_FOR_CLIENT_APPROVAL,
    });
  };

  return {
    task: {
      id: 'send-post-for-approval',
      title: t('onboardingChecklist.tasks.sendPostForApproval.title'),
      description: t(
        'onboardingChecklist.tasks.sendPostForApproval.description',
      ),
      ctaLabel: hasAnyPost
        ? t('onboardingChecklist.tasks.sendPostForApproval.cta')
        : t('onboardingChecklist.tasks.createFirstPost.cta'),
      isDone: hasSentPostForApproval,
      perform: hasAnyPost ? handleSendForApproval : () => createPost(),
    },
  };
};
